import {
  assertStudioCapability,
  isStudioCapability,
} from "./StudioCapability";
import {
  StudioPluginContractError,
} from "./errors";

function isNonEmptyString(value) {
  return typeof value === "string" && value.trim() !== "";
}

function isPlainObject(value) {
  if (value === null || typeof value !== "object") {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);

  return prototype === Object.prototype || prototype === null;
}

function normalizeStringList(values, fieldName) {
  if (!Array.isArray(values)) {
    throw new StudioPluginContractError(
      `StudioPlugin.${fieldName} must be an array.`,
      `INVALID_PLUGIN_${fieldName.toUpperCase()}`
    );
  }

  const normalized = values.map((value) => {
    if (!isNonEmptyString(value)) {
      throw new StudioPluginContractError(
        `StudioPlugin.${fieldName} must contain only non-empty strings.`,
        `INVALID_PLUGIN_${fieldName.toUpperCase()}`
      );
    }

    return value.trim();
  });

  if (new Set(normalized).size !== normalized.length) {
    throw new StudioPluginContractError(
      `StudioPlugin.${fieldName} must not contain duplicates.`,
      `DUPLICATE_PLUGIN_${fieldName.toUpperCase()}`
    );
  }

  return Object.freeze(normalized);
}

function normalizeCapabilities(capabilities) {
  if (!Array.isArray(capabilities)) {
    throw new StudioPluginContractError(
      "StudioPlugin.capabilities must be an array.",
      "INVALID_PLUGIN_CAPABILITIES"
    );
  }

  const normalized = capabilities.map(assertStudioCapability);
  const ids = normalized.map((capability) => capability.id);

  if (new Set(ids).size !== ids.length) {
    throw new StudioPluginContractError(
      "StudioPlugin.capabilities must have unique identifiers.",
      "DUPLICATE_PLUGIN_CAPABILITY"
    );
  }

  return Object.freeze([...normalized]);
}

export function createStudioPlugin({
  id,
  version = "1.0.0",
  capabilities = [],
  requires = [],
  metadata = {},
} = {}) {
  if (!isNonEmptyString(id)) {
    throw new StudioPluginContractError(
      "StudioPlugin.id must be a non-empty string.",
      "INVALID_PLUGIN_ID"
    );
  }

  if (!isNonEmptyString(version)) {
    throw new StudioPluginContractError(
      "StudioPlugin.version must be a non-empty string.",
      "INVALID_PLUGIN_VERSION"
    );
  }

  if (!isPlainObject(metadata)) {
    throw new StudioPluginContractError(
      "StudioPlugin.metadata must be a plain object.",
      "INVALID_PLUGIN_METADATA"
    );
  }

  return Object.freeze({
    id: id.trim(),
    version: version.trim(),
    capabilities: normalizeCapabilities(capabilities),
    requires: normalizeStringList(requires, "requires"),
    metadata: Object.freeze({ ...metadata }),
  });
}

export function isStudioPlugin(value) {
  return Boolean(
    value
      && typeof value === "object"
      && isNonEmptyString(value.id)
      && isNonEmptyString(value.version)
      && Array.isArray(value.capabilities)
      && value.capabilities.every(isStudioCapability)
      && Array.isArray(value.requires)
      && value.requires.every(isNonEmptyString)
      && isPlainObject(value.metadata)
  );
}

export function assertStudioPlugin(value) {
  if (!isStudioPlugin(value)) {
    throw new StudioPluginContractError(
      "Expected a valid StudioPlugin.",
      "INVALID_STUDIO_PLUGIN"
    );
  }

  return value;
}
